import { ReactNode } from 'react'

import { Placement } from '@floating-ui/react'

export type ColumnKey = string

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CellValue = any

export interface ColumnRendererContext<RowType> {
  value: CellValue
  rowData: RowType
  rowIndex: number
}

export type ColumnRenderer<RowType> = ({
  value,
  rowData,
}: ColumnRendererContext<RowType>) => ReactNode

export interface ColumnConfig<RowType> {
  title?: string // used by Columns Manager
  columnKey: Exclude<keyof RowType, symbol | number> | ColumnKey // Unique identifier for column (could use dot-notation e.g. "region.name")
  header: ReactNode
  width?: string
  minWidth?: string
  renderer?: ColumnRenderer<RowType>
  groupId?: string
  contentAlign?: 'center' | 'left' | 'right'
  shouldSpanRowWithSameValue?: boolean // Only a single column can have this property as true
}

export interface CellClickArgs<RowType> {
  column: ColumnConfig<RowType>
  rowIndex: number
  columnIndex: number
  value: CellValue
  rowData: RowType
  groupId?: string
  target: Element
}

export interface HeaderCellClickArgs {
  columnKey: ColumnKey
  columnIndex: number
  groupId?: string
  target: Element
  shiftKey: boolean
}

export type GroupByRenderer = (groupByFieldValue: string) => ReactNode
export type GroupByCellRenderer = (args: {
  groupRowsByFieldValue: string
  columnIndex: number
  columnKey: ColumnKey
}) => ReactNode

export interface DataGridProps<RowType> {
  children?: ReactNode
  className?: string
  columnGroupHeaderRenderer?: (groupId: string) => ReactNode
  columns: ColumnConfig<RowType>[]
  data: RowType[]
  extensions?: Extension<RowType>[]
  gridId?: string // Please pass gridId if isResizable is set to true, this is used to cache the size of the columns for that grid
  gridTheme?: DataGridThemeProp
  groupRowsByField?: keyof RowType
  isResizable?: boolean
  onCellClick?: (cellClickArgs: CellClickArgs<RowType>) => void
  onHeaderCellClick?: (cellClickArgs: HeaderCellClickArgs) => void
  pinnedColumns?: number
  pinnedColumnsRight?: number
  pinnedRows?: number
  rowsGroupHeaderCellRenderer?: GroupByCellRenderer
  rowsGroupHeaderRowRenderer?: GroupByRenderer
  selectedRowIndexes?: number[]
  selectedColumnIndexes?: number[]
  skipFromGroupingField?: keyof RowType
  virtualization?: VirtualizationConfig
}

declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface DefaultTheme extends DataGridTheme {}
}

export interface ContentCellContext<RowType> {
  rowIndex: number
  columnIndex: number
  value: CellValue
  column: ColumnConfig<RowType>
  rowData: RowType
}

export interface HeaderCellContext {
  columnIndex: number
  columnKey: ColumnKey
}

export interface Extension<RowType> {
  getCustomContentCellClassName?: (context: ContentCellContext<RowType>) => string
  getCustomHeaderCellClassName?: (context: HeaderCellContext) => string
}

export enum ColumnPinning {
  None,
  Left,
  Right,
}

export enum LeftRight {
  Left = 'left',
  Right = 'right',
}

export interface VirtualizationConfig {
  initialVisibleRows: number
  scrollOffsetRows: number
  rowHeight: number
}

export interface DataGridThemeHeader {
  color: string
  background: string
  bottomBorderWidth: number
  groupColor: string
  groupBackground: string
}

export interface DataGridTheme {
  borderColor?: string
  borderColorResize?: string
  borderColorGroupsAndPinned?: string
  borderWidth: number
  borderWidthHorizontal: number
  borderWidthVertical: number
  evenRowBackground: string
  firstColumnPadding: number
  fontSize: number
  header: DataGridThemeHeader
  oddRowBackground: string
  outerBorderWidth: number
  rowsGroupHeaderBackground: string
  selectedRowBackground: string
}

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

export type DataGridThemeProp = DeepPartial<DataGridTheme>

export interface ResizedColumnsParams {
  columnKey: string
  updatedColumnWidth: number
}

export type FilterPlacement = Placement
